import { Typography } from 'design-system/src/components/Typography/Typography';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';
import { FileUploader, UploadedFileRow, UploadedFile } from '../Files';
import { FormLabel } from '../../FormLabel';
import type { NewCommentFile, FileType } from '../../../api';

export type FileUploadFieldError = {
  message: string;
  visible: boolean;
};

export type FileUploadFieldProps = {
  /** Optional label displayed above the field */
  label?: string;
  /** ID for the label element (for accessibility) */
  labelId?: string;
  /** Instruction text displayed to the user */
  instructions: React.ReactNode;
  /** Currently uploaded files */
  uploadedFiles: NewCommentFile[];
  /** Handler called when a file is deleted */
  onFileDelete: (filename: string, documentType: FileType) => void;
  /** Handler called when new files are uploaded */
  onFilesUpload: (files: NewCommentFile[]) => void;
  /** File type options for the uploader */
  fileTypesOptions: FileType[];
  /** Array of error messages to display */
  errors?: FileUploadFieldError[];
  /** Whether to show "Optional" indicator next to the label */
  optional?: boolean;
};

/**
 * A reusable file upload field with instructions, file list, and error handling.
 * Used across document upload and insurance input sections.
 */
const FileUploadField: React.FC<FileUploadFieldProps> = ({
  label,
  labelId,
  instructions,
  uploadedFiles,
  onFileDelete,
  onFilesUpload,
  fileTypesOptions,
  errors = [],
  optional = false,
}) => {
  return (
    <div className="flex flex-1 flex-col justify-center gap-3">
      {label && labelId ? (
        <div>
          <div className="flex items-baseline gap-1.5">
            <FormLabel id={labelId}>{label}</FormLabel>
            {optional && (
              <Typography styledAs="bodyExtraSmallSpaceGrotesk" color="var(--grayscale-500)">
                Optional
              </Typography>
            )}
          </div>
          <Typography styledAs="bodySmallDMSans">{instructions}</Typography>
        </div>
      ) : (
        <Typography styledAs="bodySmallDMSans">{instructions}</Typography>
      )}

      {uploadedFiles.length > 0 && (
        <UploadedFileRow>
          {uploadedFiles.map((fileInfo) => (
            <UploadedFile
              key={`${fileInfo.file.name}-${fileInfo.document_type}`}
              filename={fileInfo.file.name}
              fileType={fileInfo.document_type}
              onFileDelete={onFileDelete}
            />
          ))}
        </UploadedFileRow>
      )}

      <FileUploader onFilesUpload={onFilesUpload} fileTypesOptions={fileTypesOptions} showAttachButtonLabel />

      {errors.map((error, index) => (
        <ErrorMessage key={index} visible={error.visible} marginTop="8px">
          <Typography styledAs="bodySmallDMSans">{error.message}</Typography>
        </ErrorMessage>
      ))}
    </div>
  );
};

export default FileUploadField;
